import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component,
    ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {HorizontalSkewService} from './horizontal-skew.service';
import {HorizontalSkewChartPopupComponent} from './horizontal-skew-chart-popup.component';
import {DetectMethodChanges} from '../utils';

/**
 * Per-panel Horizontal-Skew surface (CR-20260701-001, R3; re-homed to the CALLS/PUTS
 * side-panel headers per owner vrequest 2026-07-02, R9) — a bare `N.NN` reading box
 * styled as a visual twin of the sibling header boxes (IV / Vertical Skew), identified
 * by its format and tooltip rather than a label, plus the integrated settings overlay:
 * centered reading, label-above day-count number boxes (DX controls), a DX Spot/Forward
 * radio group, the cheat-sheet, and a chart button in the TOP-RIGHT corner that opens
 * the shared term-structure chart popup.
 *
 * This component is a PURE VIEW: the hosting panel (OPG / Position Modifier) provides
 * ONE `HorizontalSkewService` at the component level and owns its lifecycle
 * (init / setUnderlying / dispose in the panel base). Both header instances (calls +
 * puts) inject that same service, so their readings and settings are always identical
 * and the backend sees a single subscription per panel. Clicks stop propagation — the
 * header hosts other click behavior (label click collapses the side).
 */
@Component({
    selector: 'ets-horizontal-skew-panel-indicator',
    templateUrl: './horizontal-skew-panel-indicator.component.html',
    styleUrls: ['./horizontal-skew-panel-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorizontalSkewPanelIndicatorComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _service: HorizontalSkewService,
        private readonly _elementRef: ElementRef
    ) {
    }

    private readonly _unsubscriber = new Subject<void>();

    @ViewChild(HorizontalSkewChartPopupComponent)
    chartPopup: HorizontalSkewChartPopupComponent;

    /** Display label for the underlying (falls back to the raw ticker). */
    @Input() displayName: string;

    overlayVisible = false;
    overlayStyle: { [key: string]: string } = {};

    // Spot/Forward radio group (dx-radio-group items).
    readonly basisItems = [
        {text: 'Spot', value: 'spot'},
        {text: 'Forward', value: 'forward'},
    ];

    // Cheat-sheet, ordered high → low like a thermometer (Backwardation at the top).
    readonly bands = [
        {
            key: 'backwardation', label: 'Backwardation ( > 1.0 )',
            meaning: 'Short-term insurance costs more than long-term — the market is paying up for immediate protection.'
        },
        {
            key: 'transitioning', label: 'Transitioning ( = 1.0 )',
            meaning: 'The market is evenly balanced across near and far.'
        },
        {
            key: 'contango', label: 'Contango ( < 1.0 )',
            meaning: 'Long-term insurance costs more than short-term — the normal order of things.'
        },
    ];

    get loading(): boolean {
        return this._service.loading;
    }

    get displayRatio(): string {
        return this._service.ratio != null ? this._service.ratio.toFixed(2) : 'N/A';
    }

    get short(): number {
        return this._service.short;
    }

    get long(): number {
        return this._service.long;
    }

    get basis(): string {
        return this._service.basis;
    }

    get band(): string | null {
        return this._service.band;
    }

    get shortLegLabel(): string | null {
        return this._service.shortLegLabel;
    }

    get longLegLabel(): string | null {
        return this._service.longLegLabel;
    }

    get displayLabel(): string {
        return this.displayName || this._service.underlying || '';
    }

    ngOnInit(): void {
        this._service.updated$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this._changeDetector.detectChanges());
    }

    ngOnDestroy(): void {
        // View only — the OWNING panel disposes the shared service exactly once.
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    /** Band colour for the OVERLAY ratio only — the header reading stays plain like its siblings. */
    colorClass(): string {
        const b = this._service.band;
        if (b == null) { return 'hs-na'; }
        if (b === 'backwardation') { return 'hs-red'; }
        if (b === 'contango') { return 'hs-blue'; }
        return 'hs-neutral';
    }

    isBandActive(key: string): boolean {
        return this._service.band === key;
    }

    @DetectMethodChanges()
    toggleOverlay(): void {
        this.overlayVisible = !this.overlayVisible;
        if (this.overlayVisible) {
            const box = this._elementRef.nativeElement.querySelector('.hs-box');
            if (box) {
                const rect = box.getBoundingClientRect();
                // Open DOWNWARD from the header, centered on the reading, clamped on-screen.
                let left = rect.left + (rect.width / 2) - 150;
                left = Math.max(4, Math.min(left, window.innerWidth - 304));
                this.overlayStyle = {
                    left: left + 'px',
                    top: (rect.bottom + 4) + 'px'
                };
            }
        }
    }

    /** Chart button (top-right of the overlay): open the standalone term-structure chart. */
    openChart(): void {
        const underlying = this._service.underlying;
        if (!underlying || !this.chartPopup) { return; }
        // The chart reads the surface's CURRENT basis — it has no switcher of its own.
        this.chartPopup.open(underlying, this._service.basis, this.displayLabel);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.overlayVisible) { return; }
        if (this._elementRef.nativeElement.contains(event.target)) { return; }
        // DevExtreme editors teleport their popups to <body> (.dx-overlay-wrapper) —
        // clicks in them are logically inside this overlay, physically outside it.
        const el = event.target as Element;
        if (el && el.closest && el.closest('.dx-overlay-wrapper')) { return; }
        this.overlayVisible = false;
        this._changeDetector.detectChanges();
    }

    // DX fires onValueChanged for programmatic [value] updates too — `$event.event`
    // discriminates a USER entry (programmatic re-resolves must not loop back in).
    @DetectMethodChanges({isAsync: true})
    async onShortChanged(e: any): Promise<void> {
        if (!e || !e.event) { return; }
        const applied = await this._service.setLegs(e.value, this._service.long);
        // A rejected entry (e.g. short >= long) left the widget showing a value the
        // service refused; the unchanged [value] binding can't correct it — write the
        // truth back (the programmatic set fires onValueChanged without e.event).
        if (!applied && e.component) {
            e.component.option('value', this._service.short);
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onLongChanged(e: any): Promise<void> {
        if (!e || !e.event) { return; }
        const applied = await this._service.setLegs(this._service.short, e.value);
        if (!applied && e.component) {
            e.component.option('value', this._service.long);
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onBasisChanged(e: any): Promise<void> {
        if (!e || !e.event) { return; }
        await this._service.setBasis(e.value);
    }
}
